import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock } from "lucide-react";
import { EcGift as Gift } from "@/components/icons/EcGift";
import { EcMark } from "@/components/icons/EcMark";
import { useReferralTracking } from '@/hooks/useReferralTracking';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

export function ReferralSidePopup() {
  const { referralDiscount, hasActiveDiscount, getTimeRemaining } = useReferralTracking();
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const t = (el: string, en: string) => (language === 'el' ? el : en);

  // Check if we should show the popup (only on first visit with referral)
  useEffect(() => {
    if (!hasActiveDiscount || !referralDiscount) return;
    
    // Check if we've already shown the popup in this session
    const sessionKey = `referral_popup_shown_${referralDiscount.referrerCode}`;
    const alreadyShown = sessionStorage.getItem(sessionKey);
    
    if (!alreadyShown) {
      // Show popup after a delay - longer on mobile for better performance
      const delay = isMobile ? 8000 : 1500;
      const timer = setTimeout(() => {
        setIsVisible(true);
        sessionStorage.setItem(sessionKey, 'true');
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [hasActiveDiscount, referralDiscount, isMobile]);

  // Update countdown
  useEffect(() => {
    if (!hasActiveDiscount) return;
    
    const updateTime = () => {
      const remaining = getTimeRemaining();
      if (remaining) {
        setTimeLeft({
          hours: remaining.hours,
          minutes: remaining.minutes,
          seconds: remaining.seconds,
        });
      }
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [hasActiveDiscount, getTimeRemaining]);

  if (!isVisible || !referralDiscount) return null;

  const formatTime = (value: number) => value.toString().padStart(2, '0');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 100, opacity: 0, scale: 0.9 }}
        animate={{ x: 0, opacity: 1, scale: 1 }}
        exit={{ x: 100, opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="fixed bottom-6 right-6 z-50 max-w-sm w-full"
      >
        <div className="relative overflow-hidden rounded-2xl shadow-2xl">
          {/* Gradient background - no complex patterns on mobile for performance */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-[hsl(var(--ec-mint))]" />
          {!isMobile && (
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNGMwIDItMiA0LTIgNHMtMi0yLTItNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
          )}
          
          {/* Content */}
          <div className="relative p-6">
            {/* Close button */}
            <button
              onClick={() => setIsVisible(false)}
              className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Header with icon - no backdrop-blur on mobile */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full bg-white/20 flex items-center justify-center ${!isMobile ? 'backdrop-blur-sm' : ''}`}>
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <EcMark className="w-4 h-4 text-[hsl(var(--warning))]" />
                  <span className="text-xs font-medium text-[hsl(var(--warning))] uppercase tracking-wider">
                    Referral Bonus
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white">
                  {t('Έχεις Έκπτωση!', 'You have a discount!')}
                </h3>
              </div>
            </div>
            
            {/* Discount display */}
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-4xl font-bold text-white">
                    {referralDiscount.discountPercent}%
                  </span>
                  <span className="text-white/80 ml-2">{t('έκπτωση', 'discount')}</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/70 mb-1">{t('Σε όλα τα πακέτα', 'On all packages')}</p>
                  <p className="text-sm font-medium text-[hsl(var(--warning))]">One-off plans</p>
                </div>
              </div>
            </div>
            
            {/* Countdown */}
            {timeLeft && (
              <div className="flex items-center justify-center gap-3 text-white">
                <Clock className="w-4 h-4 text-white/70" />
                <span className="text-sm text-white/80">{t('Λήγει σε', 'Expires in')}</span>
                <div className="flex items-center gap-1 font-mono font-bold">
                  <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.hours)}</span>
                  <span>:</span>
                  <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.minutes)}</span>
                  <span>:</span>
                  <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.seconds)}</span>
                </div>
              </div>
            )}
            
            {/* Subtle note */}
            <p className="text-xs text-center text-white/60 mt-4">
              {t('Η έκπτωση εφαρμόζεται αυτόματα στο checkout', 'Discount is applied automatically at checkout')}
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
